import type { AdminQueryPort } from "../../contracts/admin-query-port";
import type {
  AdminDeviceKpiPage,
  AdminDeviceKpiQuery,
  AdminFailedFlowPage,
  AdminFailedFlowQuery,
  AdminIncidentLogArtifactAccessAction,
  AdminIncidentLogArtifactAccessGrant,
  AdminJobPage,
  AdminJobQuery,
  AdminMediaArtifactAccessAction,
  AdminMediaArtifactAccessGrant,
  AdminOverviewQuery,
  AdminOverviewSummary,
  AdminQueryEnvelope,
  AdminReleaseRegression,
  AdminReleaseRegressionQuery,
  AdminUserDataDetail,
  AdminUserLookupInput,
  AdminUserSummaryPage,
  AdminUserSummaryQuery,
  AdminUserSummary,
} from "../../contracts/admin-read-models";

export type AdminQueryFixtureSet = {
  overview: AdminQueryEnvelope<AdminOverviewSummary>;
  failedFlows: AdminQueryEnvelope<AdminFailedFlowPage>;
  deviceKpis: AdminQueryEnvelope<AdminDeviceKpiPage>;
  userSummaries: AdminQueryEnvelope<AdminUserSummaryPage>;
  userSummary: AdminQueryEnvelope<AdminUserSummary>;
  userDataDetail: AdminQueryEnvelope<AdminUserDataDetail>;
  mediaArtifactAccess: AdminQueryEnvelope<AdminMediaArtifactAccessGrant>;
  incidentLogArtifactAccess: AdminQueryEnvelope<AdminIncidentLogArtifactAccessGrant>;
  jobs: AdminQueryEnvelope<AdminJobPage>;
  releaseRegression: AdminQueryEnvelope<AdminReleaseRegression>;
};

function cloneFixture<T>(value: T): T {
  return structuredClone(value);
}

function createDefaultFixtureSet(): AdminQueryFixtureSet {
  return {
    overview: {
      data: {
        timeRange: {
          from: "2026-06-11T00:00:00.000Z",
          to: "2026-06-12T00:00:00.000Z",
        },
        requestCount: 125,
        serverErrorCount: 6,
        clientErrorCount: 9,
        topErrorCodes: [
          {
            errorCode: "AI_ANALYSIS_FAILED",
            count: 4,
            signalKey: "health_record",
            stage: "health_analysis_process",
            topFeature: "records",
            topUseCase: "CreateHealthRecordDraft",
          },
          {
            errorCode: "RECEIPT_EXTRACTION_FAILED",
            count: 2,
            signalKey: "receipt_record",
            stage: "receipt_extract",
            topFeature: "receipts",
            topUseCase: "ExtractReceiptDraft",
          },
        ],
        useCaseHealth: [
          {
            feature: "records",
            useCase: "CreateHealthRecordDraft",
            signalKey: "health_record",
            stage: "health_analysis_process",
            successCount: 54,
            failureCount: 4,
            successRate: 0.931,
          },
          {
            feature: "receipts",
            useCase: "ExtractReceiptDraft",
            signalKey: "receipt_record",
            stage: "receipt_extract",
            successCount: 21,
            failureCount: 2,
            successRate: 0.913,
          },
          {
            feature: "settings",
            useCase: "RequestAccountDeletion",
            signalKey: "account_deletion",
            stage: "account_deletion_request",
            successCount: 8,
            failureCount: 1,
            successRate: 0.889,
          },
        ],
        jobHealth: [
          {
            jobType: "health_file_analysis",
            signalKey: "health_record",
            queuedCount: 2,
            failedCount: 1,
            succeededCount: 14,
          },
          {
            jobType: "daily_analysis",
            signalKey: "daily_record",
            queuedCount: 1,
            failedCount: 1,
            succeededCount: 9,
          },
          {
            jobType: "account_deletion",
            signalKey: "account_deletion",
            queuedCount: 1,
            failedCount: 0,
            succeededCount: 6,
          },
        ],
        releaseHealth: [
          {
            release: "2026.06.12-1",
            appVersion: "1.3.0",
            requestCount: 98,
            failureCount: 5,
            topErrorCode: "AI_ANALYSIS_FAILED",
          },
          {
            release: "2026.06.11-2",
            appVersion: "1.2.9",
            requestCount: 27,
            failureCount: 1,
            topErrorCode: "RECEIPT_EXTRACTION_FAILED",
          },
        ],
        aiRuntimeOperations: {
          aiProviderRouteLabel: "Gemini Developer API",
          aiQuotaState: "Healthy",
          aiServingAccessState: "Permission denied",
          aiBudgetThresholdState: "Healthy",
          aiProviderLatencyBucket: "Stable",
          manualReviewFallbackPressure: "Elevated",
          aiModelUsageLineage: "gemini-3.1-flash-lite",
          aiModelUsageSummary: "gemini-3.1-flash-lite 78%, gemini-3.1-pro 22%",
          aiModelLimitSummary: "gemini-3.1-flash-lite near limit",
        },
        observabilityAggregate: {
          businessFlowCompletionRate: "Stable",
          failureReasonBucketSummary: "AUTH_EXPIRED dominant",
          releaseErrorSpikeSummary: "Contained",
          jobRetryPressure: "Low",
          oldestQueuedAgeBucket: "Under 15 min",
          providerFailureDistribution: "Gemini route dominant",
        },
      },
      meta: {
        requestId: "fixture_overview_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
        },
      },
    },
    failedFlows: {
      data: {
        items: [
          {
            id: "flow_1",
            createdAt: "2026-06-12T00:10:00.000Z",
            feature: "records",
            useCase: "CreateHealthRecordDraft",
            signalKey: "health_record",
            stage: "health_analysis_process",
            status: "failed",
            requestId: "req_flow_1",
            correlationId: "corr_flow_1",
            jobId: "job_1",
            userIdHash: "user_hash_1",
            accountIdentity: {
              accountEmail: "guardian.one@petlog.com",
              emailVerified: true,
              displayName: "Guardian One",
              provider: "google",
              providerEmail: "guardian.one@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian One",
              providerLinkedAt: "2026-05-20T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            petIdHash: "pet_hash_1",
            fileId: "file_1",
            errorCode: "AI_ANALYSIS_FAILED",
            statusCode: 500,
            release: "2026.06.12-1",
            appVersion: "1.3.0",
            platform: "ios",
            runtimeAppBuildNumber: "7001",
            runtimeLocale: "ko",
            runtimeTimezoneOffsetBucket: "utc_plus_4_to_plus_9",
            runtimeTimezoneNameBucket: "asia_seoul",
            runtimeDeviceModelBucket: "apple_phone",
            runtimeDeviceModelNameBucket: "apple_iphone",
            runtimeOsMajorBucket: "ios_18_plus",
            runtimeOsVersionMinorBucket: "ios_18_1_x",
            runtimeNetworkTypeBucket: "wifi",
            runtimePhysicalDeviceBucket: "physical",
            requestSuggestionUsed: true,
            requestProfileCompletionStep: "profile_edit",
            requestAttemptCountBucket: "1",
            requestPhotoConfirmLatencyBucket: "5s_to_30s",
            requestSpeciesBucket: "dog",
            requestBreedSelectionBucket: "catalog_match",
            requestHealthFileSizeBucket: "1mb_to_5mb",
            requestHealthFileTypeBucket: "pdf",
            requestHealthAnalysisFallbackPath: "manual_timeout",
            requestHealthConfirmToSaveLatencyBucket: "10s_to_60s",
            deviceInstallId: "device_hash_1",
            lastEventName: "health_file_analysis_failed",
            lastEventAt: "2026-06-12T00:10:20.000Z",
          },
          {
            id: "flow_2",
            createdAt: "2026-06-11T23:40:00.000Z",
            feature: "receipts",
            useCase: "ExtractReceiptDraft",
            signalKey: "receipt_record",
            stage: "receipt_extract",
            status: "partial",
            requestId: "req_flow_2",
            correlationId: "corr_flow_2",
            userIdHash: "user_hash_2",
            accountIdentity: {
              accountEmail: "guardian.two@petlog.com",
              emailVerified: true,
              displayName: "Guardian Two",
              provider: "apple",
              providerEmail: "guardian.two@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian Two",
              providerLinkedAt: "2026-06-01T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            receiptId: "receipt_1",
            fileId: "file_receipt_1",
            errorCode: "RECEIPT_EXTRACTION_FAILED",
            statusCode: 502,
            release: "2026.06.12-1",
            appVersion: "1.3.0",
            platform: "android",
            runtimeAppBuildNumber: "6104",
            runtimeLocale: "en",
            runtimeTimezoneOffsetBucket: "utc_minus_4_to_plus_3",
            runtimeTimezoneNameBucket: "north_america_eastern",
            runtimeDeviceModelBucket: "android_phone",
            runtimeDeviceModelNameBucket: "google_pixel",
            runtimeOsMajorBucket: "android_14_plus",
            runtimeOsVersionMinorBucket: "android_14_1_plus",
            runtimeNetworkTypeBucket: "cellular",
            runtimePhysicalDeviceBucket: "simulator_or_emulator",
            deviceInstallId: "device_hash_2",
            lastEventName: "provider.fallback",
            lastEventAt: "2026-06-11T23:40:10.000Z",
          },
        ],
        totalCount: 2,
        observabilityAggregate: {
          failureReasonBucketSummary: "AUTH_EXPIRED dominant",
        },
      },
      meta: {
        requestId: "fixture_failed_flows_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
          logs: "fresh",
        },
        nextCursor: "fixture_failed_flows_cursor_2",
      },
    },
    deviceKpis: {
      data: {
        items: [
          {
            deviceInstallId: "device_hash_1",
            platform: "ios",
            appVersion: "1.3.0",
            release: "2026.06.12-1",
            lastSeenAt: "2026-06-12T00:10:20.000Z",
            sessionStartCount: 4,
            authSuccessCount: 2,
            petCreateSuccessCount: 1,
            petUpdateSuccessCount: 1,
            petDeleteSuccessCount: 0,
            petPhotoConfirmSuccessCount: 1,
            todayStatusSaveSuccessCount: 3,
            dailySpeechCaptureStartCount: 2,
            dailySpeechCaptureCancelCount: 1,
            dailyDraftCreateSuccessCount: 2,
            dailyDraftUpdateCount: 2,
            dailyDraftDiscardCount: 1,
            dailyRecordSaveSuccessCount: 2,
            dailyMediaUpdateSuccessCount: 1,
            healthFileUploadSuccessCount: 2,
            healthFileConfirmSuccessCount: 1,
            healthAnalysisStartCount: 1,
            healthRecordSaveSuccessCount: 1,
            receiptExtractCount: 0,
            receiptSaveSuccessCount: 1,
            receiptCategoryCreateSuccessCount: 0,
            receiptCategoryUpdateSuccessCount: 0,
            receiptCategoryDeleteSuccessCount: 0,
            socialSuccessCount: 3,
            chatSuccessCount: 4,
            communitySuccessCount: 2,
            medicationSuccessCount: 1,
            rideRequestSuccessCount: 2,
            failureCount: 2,
            topErrorCode: "AI_ANALYSIS_FAILED",
            latestUserIdHash: "user_hash_1",
            latestUserAccountIdentity: {
              accountEmail: "guardian.one@petlog.com",
              emailVerified: true,
              displayName: "Guardian One",
              provider: "google",
              providerEmail: "guardian.one@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian One",
              providerLinkedAt: "2026-05-20T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            latestRuntimeAppBuildNumber: "7001",
            latestRuntimeLocale: "ko",
            latestRuntimeTimezoneOffsetBucket: "utc_plus_4_to_plus_9",
            latestRuntimeTimezoneNameBucket: "asia_seoul",
            latestRuntimeDeviceModelBucket: "apple_phone",
            latestRuntimeDeviceModelNameBucket: "apple_iphone",
            latestRuntimeOsMajorBucket: "ios_18_plus",
            latestRuntimeOsVersionMinorBucket: "ios_18_1_x",
            latestRuntimeNetworkTypeBucket: "wifi",
            latestRuntimePhysicalDeviceBucket: "physical",
            latestRuntimeContextAt: "2026-06-12T00:10:05.000Z",
            latestPetProfileCompletionStep: "profile_edit",
            latestPetAttemptCountBucket: "1",
            latestPetSpeciesBucket: "dog",
            latestPetBreedSelectionBucket: "catalog_match",
            latestPetContextAt: "2026-06-12T00:10:03.000Z",
            latestSocialTargetUserLookupKind: "path_user_id",
            latestSocialPublicPetCountBucket: "2_to_3",
            latestSocialProfileMutationFieldSetBucket: "display_name_and_bio",
            latestSocialContextAt: "2026-06-12T00:10:02.500Z",
            latestChatRoomKind: "group",
            latestChatMessageLengthBucket: "21_to_100",
            latestChatMembershipCountBucket: "2_to_5",
            latestChatContextAt: "2026-06-12T00:10:02.700Z",
            latestMedicationScheduleRecurrenceBucket: "weekly",
            latestMedicationDosePresence: "present",
            latestMedicationOccurrenceActionKind: "snooze",
            latestMedicationSnoozeWindowBucket: "30m_to_2h",
            latestMedicationContextAt: "2026-06-12T00:10:02.900Z",
            latestRideVisitLeadTimeBucket: "2h_to_24h",
            latestRideLongestAddressLengthBucket: "41_to_120",
            latestRideMemoProvided: false,
            latestRideContextAt: "2026-06-12T00:10:02.000Z",
            latestDailySpeechDurationBucket: "5s_to_30s",
            latestDailySpeechDurationBucketAt: "2026-06-12T00:09:40.000Z",
            latestDailyDraftEditCountBucket: "2_to_5",
            latestDailyDraftEditCountBucketAt: "2026-06-12T00:09:55.000Z",
            latestDailyDraftDiscardReasonCategory: "user_discard",
            latestDailyDraftDiscardReasonCategoryAt: "2026-06-12T00:10:00.000Z",
          },
          {
            deviceInstallId: "device_hash_2",
            platform: "android",
            appVersion: "1.3.0",
            release: "2026.06.12-1",
            lastSeenAt: "2026-06-11T23:40:10.000Z",
            sessionStartCount: 2,
            authSuccessCount: 1,
            petCreateSuccessCount: 0,
            petUpdateSuccessCount: 0,
            petDeleteSuccessCount: 0,
            petPhotoConfirmSuccessCount: 0,
            todayStatusSaveSuccessCount: 1,
            dailySpeechCaptureStartCount: 1,
            dailySpeechCaptureCancelCount: 1,
            dailyDraftCreateSuccessCount: 1,
            dailyDraftUpdateCount: 1,
            dailyDraftDiscardCount: 0,
            dailyRecordSaveSuccessCount: 1,
            dailyMediaUpdateSuccessCount: 0,
            healthFileUploadSuccessCount: 0,
            healthFileConfirmSuccessCount: 0,
            healthAnalysisStartCount: 0,
            healthRecordSaveSuccessCount: 0,
            receiptExtractCount: 1,
            receiptSaveSuccessCount: 2,
            receiptCategoryCreateSuccessCount: 1,
            receiptCategoryUpdateSuccessCount: 1,
            receiptCategoryDeleteSuccessCount: 0,
            socialSuccessCount: 0,
            chatSuccessCount: 1,
            communitySuccessCount: 0,
            medicationSuccessCount: 2,
            rideRequestSuccessCount: 1,
            failureCount: 1,
            topErrorCode: "RECEIPT_EXTRACTION_FAILED",
            latestUserIdHash: "user_hash_2",
            latestUserAccountIdentity: {
              accountEmail: "guardian.two@petlog.com",
              emailVerified: true,
              displayName: "Guardian Two",
              provider: "apple",
              providerEmail: "guardian.two@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian Two",
              providerLinkedAt: "2026-06-01T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            latestRuntimeAppBuildNumber: "6104",
            latestRuntimeLocale: "en",
            latestRuntimeTimezoneOffsetBucket: "utc_minus_4_to_plus_3",
            latestRuntimeTimezoneNameBucket: "north_america_eastern",
            latestRuntimeDeviceModelBucket: "android_phone",
            latestRuntimeDeviceModelNameBucket: "google_pixel",
            latestRuntimeOsMajorBucket: "android_14_plus",
            latestRuntimeOsVersionMinorBucket: "android_14_1_plus",
            latestRuntimeNetworkTypeBucket: "cellular",
            latestRuntimePhysicalDeviceBucket: "simulator_or_emulator",
            latestRuntimeContextAt: "2026-06-11T23:40:05.000Z",
            latestReceiptExtractSourceBucket: "browser_file",
            latestReceiptImageByteSizeBucket: "1mb_to_5mb",
            latestReceiptOcrConfidenceBucket: "80_to_94",
            latestReceiptManualOverride: true,
            latestReceiptCategoryMutationType: "custom_update",
            latestReceiptContextAt: "2026-06-11T23:40:04.000Z",
            latestDailySpeechDurationBucket: "1s_to_5s",
            latestDailySpeechDurationBucketAt: "2026-06-11T23:39:00.000Z",
            latestDailyDraftEditCountBucket: "1",
            latestDailyDraftEditCountBucketAt: "2026-06-11T23:39:20.000Z",
          },
        ],
        totalCount: 2,
      },
      meta: {
        requestId: "fixture_device_kpis_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          admin_device_kpi_daily: "fresh",
          admin_request_outcomes: "fresh",
          client_flow_events: "fresh",
        },
        sourceKinds: {
          admin_device_kpi_daily: "projection",
          admin_request_outcomes: "request_outcome",
          client_flow_events: "client_flow_event",
        },
        nextCursor: "fixture_device_kpis_cursor_2",
      },
    },
    userSummaries: {
      data: {
        items: [
          {
            internalUserId: "user_internal_1",
            userIdHash: "user_hash_1",
            createdAt: "2026-05-20T00:00:00.000Z",
            lastLoginAt: "2026-06-12T09:12:00.000Z",
            termsConsentAt: "2026-05-20T00:02:00.000Z",
            accountIdentity: {
              accountEmail: "guardian.one@petlog.com",
              emailVerified: true,
              displayName: "Guardian One",
              provider: "google",
              providerEmail: "guardian.one@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian One",
              providerLinkedAt: "2026-05-20T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            accountDeletionStatus: "none",
            latestDeviceInstallId: "device_hash_1",
            latestPlatform: "ios",
            latestRelease: "petlog-mobile@0.0.845",
            latestAppVersion: "0.0.845",
            latestRuntimeAppBuildNumber: "7001",
            latestRuntimeLocale: "ko",
            latestRuntimeTimezoneOffsetBucket: "utc_plus_4_to_plus_9",
            latestRuntimeTimezoneNameBucket: "asia_seoul",
            latestRuntimeDeviceModelBucket: "apple_phone",
            latestRuntimeDeviceModelNameBucket: "apple_iphone",
            latestRuntimeOsMajorBucket: "ios_18_plus",
            latestRuntimeOsVersionMinorBucket: "ios_18_1_x",
            latestRuntimeNetworkTypeBucket: "wifi",
            latestRuntimePhysicalDeviceBucket: "physical",
            latestRuntimeContextAt: "2026-06-12T09:10:00.000Z",
            counts: {
              pets: 2,
              records: 8,
              dailyRecords: 4,
              healthRecords: 2,
              receipts: 2,
              files: 5,
              aiJobs: 3,
            },
            riskSummary: {
              highestLevel: "critical",
              items: [
                {
                  code: "AI_JOB_FAILED",
                  level: "critical",
                  label: "AI job failed",
                  evidence: "job_ai_1",
                },
              ],
            },
            latestFailure: {
              requestId: "req_flow_1",
              correlationId: "corr_flow_1",
              feature: "records",
              useCase: "CreateHealthRecordDraft",
              signalKey: "health_record",
              stage: "health_analysis_process",
              errorCode: "AI_ANALYSIS_FAILED",
              occurredAt: "2026-06-12T08:40:00.000Z",
            },
            latestSuccessfulSave: {
              event: "record_saved",
              occurredAt: "2026-06-12T08:00:00.000Z",
              recordType: "daily",
              recordId: "record_daily_1",
            },
            recentFailures: [],
            recentSuccessfulSaves: [],
          },
          {
            internalUserId: "user_internal_2",
            userIdHash: "user_hash_2",
            createdAt: "2026-06-01T00:00:00.000Z",
            lastLoginAt: "2026-06-12T07:12:00.000Z",
            termsConsentAt: "2026-06-01T00:01:00.000Z",
            accountIdentity: {
              accountEmail: "guardian.two@petlog.com",
              emailVerified: true,
              displayName: "Guardian Two",
              provider: "apple",
              providerEmail: "guardian.two@petlog.com",
              providerEmailVerified: true,
              providerDisplayName: "Guardian Two",
              providerLinkedAt: "2026-06-01T00:00:00.000Z",
              linkedProviderCount: 1,
            },
            accountDeletionStatus: "none",
            latestDeviceInstallId: "device_hash_2",
            latestPlatform: "android",
            latestRelease: "petlog-mobile@0.0.844",
            latestAppVersion: "0.0.844",
            latestRuntimeAppBuildNumber: "6104",
            latestRuntimeLocale: "en",
            latestRuntimeTimezoneOffsetBucket: "utc_minus_4_to_plus_3",
            latestRuntimeTimezoneNameBucket: "north_america_eastern",
            latestRuntimeDeviceModelBucket: "android_phone",
            latestRuntimeDeviceModelNameBucket: "google_pixel",
            latestRuntimeOsMajorBucket: "android_14_plus",
            latestRuntimeOsVersionMinorBucket: "android_14_1_plus",
            latestRuntimeNetworkTypeBucket: "cellular",
            latestRuntimePhysicalDeviceBucket: "simulator_or_emulator",
            latestRuntimeContextAt: "2026-06-12T07:10:00.000Z",
            counts: {
              pets: 1,
              records: 3,
              dailyRecords: 1,
              healthRecords: 1,
              receipts: 1,
              files: 2,
              aiJobs: 0,
            },
            riskSummary: {
              highestLevel: "warning",
              items: [
                {
                  code: "FILE_CLEANUP_PENDING",
                  level: "warning",
                  label: "File cleanup pending",
                  evidence: "file_2",
                },
              ],
            },
            latestFailure: {
              requestId: "req_flow_2",
              feature: "files",
              useCase: "CleanupDeletedFiles",
              signalKey: "file_lifecycle",
              stage: "file_cleanup",
              errorCode: "FILE_CLEANUP_PENDING",
              occurredAt: "2026-06-12T07:05:00.000Z",
            },
            latestSuccessfulSave: {
              event: "receipt_saved",
              occurredAt: "2026-06-11T23:00:00.000Z",
              recordType: "receipt",
              receiptId: "receipt_2",
            },
            recentFailures: [],
            recentSuccessfulSaves: [],
          },
        ],
        totalCount: 2,
      },
      meta: {
        requestId: "fixture_user_summaries_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          admin_user_summaries: "fresh",
          admin_request_outcomes: "fresh",
          admin_jobs: "partial",
        },
      },
    },
    userSummary: {
      data: {
        internalUserId: "user_internal_1",
        userIdHash: "user_hash_1",
        createdAt: "2026-05-20T00:00:00.000Z",
        lastLoginAt: "2026-06-12T00:05:00.000Z",
        termsConsentAt: "2026-05-20T00:00:00.000Z",
        accountIdentity: {
          accountEmail: "guardian.one@petlog.com",
          emailVerified: true,
          displayName: "Guardian One",
          provider: "google",
          providerEmail: "guardian.one@petlog.com",
          providerEmailVerified: true,
          providerDisplayName: "Guardian One",
          providerLinkedAt: "2026-05-20T00:00:00.000Z",
          linkedProviderCount: 1,
        },
        accountDeletionStatus: "none",
        latestDeviceInstallId: "device_hash_1",
        latestPlatform: "ios",
        latestRelease: "2026.06.12-1",
        latestAppVersion: "1.3.0",
        latestRuntimeAppBuildNumber: "7001",
        latestRuntimeLocale: "ko",
        latestRuntimeTimezoneOffsetBucket: "utc_plus_4_to_plus_9",
        latestRuntimeTimezoneNameBucket: "asia_seoul",
        latestRuntimeDeviceModelBucket: "apple_phone",
        latestRuntimeDeviceModelNameBucket: "apple_iphone",
        latestRuntimeOsMajorBucket: "ios_18_plus",
        latestRuntimeOsVersionMinorBucket: "ios_18_1_x",
        latestRuntimeNetworkTypeBucket: "wifi",
        latestRuntimePhysicalDeviceBucket: "physical",
        latestRuntimeContextAt: "2026-06-12T00:10:05.000Z",
        counts: {
          pets: 2,
          records: 7,
          dailyRecords: 4,
          healthRecords: 2,
          receipts: 1,
          files: 5,
          aiJobs: 3,
        },
        riskSummary: {
          highestLevel: "critical",
          items: [
            {
              code: "AI_JOB_FAILED",
              level: "critical",
              label: "AI job failed",
              evidence: "job_1",
            },
            {
              code: "FILE_CLEANUP_FAILED",
              level: "warning",
              label: "File cleanup failed",
              evidence: "file_2",
            },
            {
              code: "PET_PROFILE_MISSING_REQUIRED",
              level: "attention",
              label: "Pet profile missing required fields",
              evidence: "pet_2",
            },
            {
              code: "PET_PHOTO_LINK_MISSING",
              level: "warning",
              label: "Pet photo link missing",
              evidence: "pet_2",
            },
          ],
        },
        latestFailure: {
          requestId: "req_flow_1",
          correlationId: "corr_flow_1",
          feature: "records",
          useCase: "CreateHealthRecordDraft",
          signalKey: "health_record",
          stage: "health_analysis_process",
          errorCode: "AI_ANALYSIS_FAILED",
          occurredAt: "2026-06-12T00:10:00.000Z",
        },
        latestSuccessfulSave: {
          event: "record_saved",
          occurredAt: "2026-06-11T22:15:00.000Z",
          recordType: "health",
          recordId: "record_1",
        },
        recentFailures: [
          {
            requestId: "req_flow_1",
            correlationId: "corr_flow_1",
            feature: "records",
            useCase: "CreateHealthRecordDraft",
            signalKey: "health_record",
            stage: "health_analysis_process",
            errorCode: "AI_ANALYSIS_FAILED",
            occurredAt: "2026-06-12T00:10:00.000Z",
          },
          {
            requestId: "req_flow_2",
            correlationId: "corr_flow_2",
            feature: "receipts",
            useCase: "ExtractReceiptDraft",
            signalKey: "receipt_record",
            stage: "receipt_extract",
            errorCode: "RECEIPT_EXTRACTION_FAILED",
            occurredAt: "2026-06-11T23:40:00.000Z",
          },
          {
            requestId: "req_flow_3",
            correlationId: "corr_flow_3",
            feature: "records",
            useCase: "CreateDailyRecordDraft",
            signalKey: "daily_record",
            stage: "daily_draft_create",
            errorCode: "DAILY_DRAFT_ANALYSIS_FAILED",
            occurredAt: "2026-06-11T21:10:00.000Z",
          },
        ],
        recentSuccessfulSaves: [
          {
            event: "record_saved",
            occurredAt: "2026-06-11T22:15:00.000Z",
            recordType: "health",
            recordId: "record_1",
          },
          {
            event: "record_saved",
            occurredAt: "2026-06-11T20:15:00.000Z",
            recordType: "daily",
            recordId: "record_daily_1",
          },
        ],
      },
      meta: {
        requestId: "fixture_user_summary_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
          user_summary: "fresh",
        },
      },
    },
    userDataDetail: {
      data: {
        user: {
          internalUserId: "user_internal_1",
          userIdHash: "user_hash_1",
          createdAt: "2026-05-20T00:00:00.000Z",
          lastLoginAt: "2026-06-12T00:05:00.000Z",
          termsConsentAt: "2026-05-20T00:00:00.000Z",
          accountDeletionStatus: "none",
          latestRelease: "2026.06.12-1",
          latestAppVersion: "1.3.0",
          counts: {
            pets: 2,
            records: 7,
            dailyRecords: 4,
            healthRecords: 2,
            receipts: 1,
            files: 5,
            aiJobs: 3,
          },
          riskSummary: {
            highestLevel: "critical",
            items: [
              {
                code: "AI_JOB_FAILED",
                level: "critical",
                label: "AI job failed",
                evidence: "job_1",
              },
              {
                code: "FILE_CLEANUP_FAILED",
                level: "warning",
                label: "File cleanup failed",
                evidence: "file_2",
              },
              {
                code: "PET_PROFILE_MISSING_REQUIRED",
                level: "attention",
                label: "Pet profile missing required fields",
                evidence: "pet_2",
              },
              {
                code: "PET_PHOTO_LINK_MISSING",
                level: "warning",
                label: "Pet photo link missing",
                evidence: "pet_2",
              },
            ],
          },
          latestFailure: {
            requestId: "req_flow_1",
            correlationId: "corr_flow_1",
            feature: "records",
            useCase: "CreateHealthRecordDraft",
            signalKey: "health_record",
            stage: "health_analysis_process",
            errorCode: "AI_ANALYSIS_FAILED",
            occurredAt: "2026-06-12T00:10:00.000Z",
          },
          latestSuccessfulSave: {
            event: "record_saved",
            occurredAt: "2026-06-11T22:15:00.000Z",
            recordType: "health",
            recordId: "record_1",
          },
          recentFailures: [
            {
              requestId: "req_flow_1",
              correlationId: "corr_flow_1",
              feature: "records",
              useCase: "CreateHealthRecordDraft",
              signalKey: "health_record",
              stage: "health_analysis_process",
              errorCode: "AI_ANALYSIS_FAILED",
              occurredAt: "2026-06-12T00:10:00.000Z",
            },
            {
              requestId: "req_flow_2",
              correlationId: "corr_flow_2",
              feature: "receipts",
              useCase: "ExtractReceiptDraft",
              signalKey: "receipt_record",
              stage: "receipt_extract",
              errorCode: "RECEIPT_EXTRACTION_FAILED",
              occurredAt: "2026-06-11T23:40:00.000Z",
            },
            {
              requestId: "req_flow_3",
              correlationId: "corr_flow_3",
              feature: "records",
              useCase: "CreateDailyRecordDraft",
              signalKey: "daily_record",
              stage: "daily_draft_create",
              errorCode: "DAILY_DRAFT_ANALYSIS_FAILED",
              occurredAt: "2026-06-11T21:10:00.000Z",
            },
          ],
          recentSuccessfulSaves: [
            {
              event: "record_saved",
              occurredAt: "2026-06-11T22:15:00.000Z",
              recordType: "health",
              recordId: "record_1",
            },
            {
              event: "record_saved",
              occurredAt: "2026-06-11T20:15:00.000Z",
              recordType: "daily",
              recordId: "record_daily_1",
            },
          ],
        },
        pets: [
          {
            petId: "pet_1",
            petIdHash: "pet_hash_1",
            createdAt: "2026-05-21T00:00:00.000Z",
            species: "dog",
            profileStatus: "complete",
            photoFileLinkStatus: "linked",
            recordCount: 4,
          },
          {
            petId: "pet_2",
            petIdHash: "pet_hash_2",
            createdAt: "2026-05-22T00:00:00.000Z",
            species: "cat",
            profileStatus: "missing_required",
            photoFileLinkStatus: "missing_file",
            recordCount: 3,
          },
        ],
        records: [
          {
            recordId: "record_1",
            type: "health",
            title: "Checkup",
            recordedAt: "2026-06-11T22:10:00.000Z",
            createdAt: "2026-06-11T22:15:00.000Z",
            linkedFileCount: 1,
            latestAiJobStatus: "succeeded",
            dataCompleteness: "complete",
          },
          {
            recordId: "record_daily_1",
            type: "daily",
            title: "Daily walk note",
            recordedAt: "2026-06-11T20:10:00.000Z",
            createdAt: "2026-06-11T20:15:00.000Z",
            linkedFileCount: 0,
            latestAiJobStatus: "queued",
            dataCompleteness: "partial",
          },
        ],
        receipts: [
          {
            receiptId: "receipt_1",
            purchasedAt: "2026-06-10T17:00:00.000Z",
            amountBucket: "under_50000",
            categoryLabel: "vet",
            createdAt: "2026-06-10T17:05:00.000Z",
            extractionStatus: "draft_created",
          },
        ],
        files: [
          {
            fileId: "file_1",
            purpose: "health_record_source",
            status: "confirmed",
            contentType: "application/pdf",
            sizeBucket: "under_10mb",
            linkedEntityType: "record",
            linkedEntityId: "record_1",
            createdAt: "2026-06-11T22:10:00.000Z",
            confirmedAt: "2026-06-11T22:11:00.000Z",
            latestLifecycleRequestId: "req_file_1_confirm",
            upstreamRequestId: "req_file_1_confirm",
          },
          {
            fileId: "file_2",
            purpose: "pet_profile_photo",
            status: "cleanup_failed",
            contentType: "image/jpeg",
            sizeBucket: "under_1mb",
            linkedEntityType: "pet",
            linkedEntityId: "pet_2",
            createdAt: "2026-06-09T09:00:00.000Z",
            expiresAt: "2026-06-13T09:00:00.000Z",
            latestLifecycleRequestId: "req_file_cleanup_2",
            upstreamRequestId: "req_pet_photo_2",
          },
        ],
        representativePhoto: {
          artifactId: "file_2",
          sourceScenario: "pet_profile",
          linkedEntityType: "pet",
          linkedEntityId: "pet_2",
          relationType: "direct",
          artifactRole: "representative",
          mediaKind: "image",
          contentTypeBucket: "image_jpeg",
          sizeBucket: "lt_1mb",
          uploadedAt: "2026-06-09T09:00:00.000Z",
          storageStatus: "cleanup_scheduled",
          previewAvailability: "deleted",
          downloadAvailability: "deleted",
          fallbackReason: "cleanup_scheduled",
          latestTraceKey: {
            requestId: "req_pet_photo_2",
            correlationId: "corr_flow_1",
          },
          auditScope: "preview_and_download",
          sameDeviceContextAvailable: false,
        },
        mediaArtifacts: [
          {
            artifactId: "file_2",
            sourceScenario: "pet_profile",
            linkedEntityType: "pet",
            linkedEntityId: "pet_2",
            relationType: "direct",
            artifactRole: "representative",
            mediaKind: "image",
            contentTypeBucket: "image_jpeg",
            sizeBucket: "lt_1mb",
            uploadedAt: "2026-06-09T09:00:00.000Z",
            storageStatus: "cleanup_scheduled",
            previewAvailability: "deleted",
            downloadAvailability: "deleted",
            fallbackReason: "cleanup_scheduled",
            latestTraceKey: {
              requestId: "req_pet_photo_2",
              correlationId: "corr_flow_1",
            },
            auditScope: "preview_and_download",
            sameDeviceContextAvailable: false,
          },
          {
            artifactId: "file_1",
            sourceScenario: "health_record",
            linkedEntityType: "record",
            linkedEntityId: "record_1",
            relationType: "contextual",
            artifactRole: "gallery_member",
            mediaKind: "document",
            contentTypeBucket: "application_pdf",
            sizeBucket: "1mb_to_10mb",
            uploadedAt: "2026-06-11T22:10:00.000Z",
            confirmedAt: "2026-06-11T22:11:00.000Z",
            storageStatus: "available",
            previewAvailability: "unsupported_type",
            downloadAvailability: "available",
            fallbackReason: "unsupported_type",
            latestTraceKey: {
              requestId: "req_file_1_confirm",
            },
            auditScope: "download_only",
            sameDeviceContextAvailable: false,
          },
          {
            artifactId: "artifact_receipt_1",
            sourceScenario: "receipt",
            linkedEntityType: "receipt",
            linkedEntityId: "receipt_1",
            relationType: "direct",
            artifactRole: "original_only",
            mediaKind: "document",
            contentTypeBucket: "application_pdf",
            sizeBucket: "1mb_to_10mb",
            uploadedAt: "2026-06-10T17:01:00.000Z",
            confirmedAt: "2026-06-10T17:02:00.000Z",
            storageStatus: "available",
            previewAvailability: "pending_policy_check",
            downloadAvailability: "available",
            latestTraceKey: {
              requestId: "req_receipt_artifact_1",
              correlationId: "corr_receipt_artifact_1",
              signalKey: "receipt_record",
              stage: "receipt_extract",
            },
            auditScope: "download_only",
            sameDeviceContextAvailable: false,
          },
        ],
        incidentLogArtifacts: [
          {
            artifactId: "incident_log_1",
            deviceInstallId: "device_install_fixture_1",
            release: "petlog-mobile@0.0.682",
            appVersion: "0.0.682",
            platform: "ios",
            runtimeAppBuildNumber: "7001",
            runtimeLocale: "ko",
            runtimeTimezoneOffsetBucket: "utc_plus_4_to_plus_9",
            runtimeTimezoneNameBucket: "asia_seoul",
            runtimeDeviceModelBucket: "apple_phone",
            runtimeDeviceModelNameBucket: "apple_iphone",
            runtimeOsMajorBucket: "ios_18_plus",
            runtimeOsVersionMinorBucket: "ios_18_1_x",
            runtimeNetworkTypeBucket: "wifi",
            runtimePhysicalDeviceBucket: "physical",
            captureTrigger: "failed_request",
            captureWindow: "pre_30s_post_30s",
            capturedAtBucket: "lt_15m",
            capturedAtOperatorValue: "2026-06-11T22:12:00.000Z",
            supportabilityState: "supported",
            excerptLineCountBucket: "21_to_100",
            bundleHashPresence: "present",
            manifestVersion: "incident_log_manifest.v1",
            redactionVersion: "incident_log_redaction.v1",
            previewReadiness: "ready",
            downloadReadiness: "ready",
            previewExcerpt: {
              headline: "Pet registration photo confirm failed after local upload recovery.",
              topCategory: "pet.photo.confirm",
              lines: [
                {
                  timestamp: "2026-06-11T22:11:48.000Z",
                  level: "info",
                  category: "pet.photo.confirm",
                  messageKey: "PET_PHOTO_CONFIRM_BEGIN",
                  redactedContext: "pet_profile flow resumed with same upload artifact",
                  requestId: "req_pet_photo_2",
                  correlationId: "corr_flow_1",
                },
                {
                  timestamp: "2026-06-11T22:11:52.000Z",
                  level: "warning",
                  category: "network.retry",
                  messageKey: "UPLOAD_RECOVERY_RETRY",
                  redactedContext: "bounded retry issued after upstream timeout bucket",
                  requestId: "req_pet_photo_2",
                  correlationId: "corr_flow_1",
                },
                {
                  timestamp: "2026-06-11T22:12:00.000Z",
                  level: "error",
                  category: "pet.photo.confirm",
                  messageKey: "PET_PHOTO_CONFIRM_FAILED",
                  redactedContext: "confirm step rejected recovered artifact with file linkage mismatch",
                  requestId: "req_pet_photo_2",
                  correlationId: "corr_flow_1",
                  errorCode: "PET_PHOTO_CONFIRM_FAILED",
                },
              ],
            },
            latestTraceKey: {
              requestId: "req_pet_photo_2",
              correlationId: "corr_flow_1",
              signalKey: "pet_profile",
              stage: "pet_photo_confirm",
              errorCode: "PET_PHOTO_CONFIRM_FAILED",
            },
            handoffReadiness: "ready",
          },
        ],
        aiJobs: [
          {
            jobId: "job_1",
            jobType: "health_file_analysis",
            signalKey: "health_record",
            status: "failed",
            attemptCount: 2,
            lastErrorCode: "AI_ANALYSIS_FAILED",
            provider: "vertex",
            queuedAt: "2026-06-12T00:09:50.000Z",
            startedAt: "2026-06-12T00:10:00.000Z",
            finishedAt: "2026-06-12T00:10:20.000Z",
            durationMs: 20000,
            linkedFileId: "file_1",
            linkedRecordId: "record_1",
          },
        ],
        account: {
          accountEmail: "guardian.one@petlog.com",
          emailVerified: true,
          displayName: "Guardian One",
          provider: "google",
          providerEmail: "guardian.one@petlog.com",
          providerEmailVerified: true,
          providerDisplayName: "Guardian One",
          providerLinkedAt: "2026-05-20T00:00:00.000Z",
          linkedProviderCount: 1,
          termsConsentAt: "2026-05-20T00:00:00.000Z",
          lastLoginAt: "2026-06-12T00:05:00.000Z",
          logoutLastSeenAt: "2026-06-11T20:00:00.000Z",
          deletionJobStatus: "none",
        },
      },
      meta: {
        requestId: "fixture_user_data_detail_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
          user_data_detail: "fresh",
        },
      },
    },
    mediaArtifactAccess: {
      data: {
        artifactId: "file_1",
        action: "download",
        accessResult: "allowed",
        issuedAt: "2026-06-12T00:00:00.000Z",
        expiresAt: "2026-06-12T00:05:00.000Z",
        url: "https://signed.example.test/file_1",
      },
      meta: {
        requestId: "fixture_media_artifact_access_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          media_access: "fresh",
        },
      },
    },
    incidentLogArtifactAccess: {
      data: {
        artifactId: "incident_log_1",
        action: "download",
        accessResult: "allowed",
        issuedAt: "2026-06-12T00:00:00.000Z",
        expiresAt: "2026-06-12T00:05:00.000Z",
        url: "https://signed.example.test/incident_log_1.json",
      },
      meta: {
        requestId: "fixture_incident_log_artifact_access_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          incident_log_access: "fresh",
        },
      },
    },
    jobs: {
      data: {
        items: [
          {
            jobId: "job_account_deletion_1",
            jobType: "account_deletion",
            signalKey: "account_deletion",
            ownerUserIdHash: "user_hash_deletion",
            status: "queued",
            attemptCount: 1,
            provider: "firebase-auth",
            queuedAt: "2026-06-12T00:10:40.000Z",
          },
          {
            jobId: "job_1",
            jobType: "health_file_analysis",
            signalKey: "health_record",
            ownerUserIdHash: "user_hash_1",
            petIdHash: "pet_hash_1",
            status: "failed",
            attemptCount: 2,
            lastErrorCode: "AI_ANALYSIS_FAILED",
            provider: "vertex",
            queuedAt: "2026-06-12T00:09:50.000Z",
            startedAt: "2026-06-12T00:10:00.000Z",
            finishedAt: "2026-06-12T00:10:20.000Z",
            durationMs: 20000,
            linkedFileId: "file_1",
            linkedRecordId: "record_1",
          },
          {
            jobId: "job_2",
            jobType: "daily_analysis",
            signalKey: "daily_record",
            ownerUserIdHash: "user_hash_1",
            status: "queued",
            attemptCount: 1,
            provider: "vertex",
            queuedAt: "2026-06-12T00:08:00.000Z",
            linkedRecordId: "record_daily_1",
          },
        ],
        totalCount: 3,
        aiRuntimeOperations: {
          aiProviderRouteLabel: "Gemini Developer API",
          aiQuotaState: "Healthy",
          aiProviderLatencyBucket: "Stable",
          aiServingAccessState: "Permission denied",
          manualReviewFallbackPressure: "Elevated",
          aiModelUsageSummary: "gemini-3.1-flash-lite 78%, gemini-3.1-pro 22%",
          aiModelLimitSummary: "Warning",
        },
        observabilityAggregate: {
          oldestQueuedAgeBucket: "Under 15 min",
          jobRetryPressure: "Low",
          terminalFailureCodeDistribution: "AUTH_EXPIRED dominant",
          providerFailureDistribution: "Gemini route dominant",
        },
      },
      meta: {
        requestId: "fixture_jobs_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
          jobs: "partial",
        },
        nextCursor: "fixture_jobs_cursor_2",
      },
    },
    releaseRegression: {
      data: {
        timeRange: {
          from: "2026-06-11T00:00:00.000Z",
          to: "2026-06-12T00:00:00.000Z",
        },
        items: [
          {
            release: "2026.06.12-1",
            appVersion: "1.3.0",
            deployedAt: "2026-06-12T00:00:00.000Z",
            requestCount: 98,
            errorRate: 0.051,
            topFailedUseCase: "CreateHealthRecordDraft",
            topErrorCode: "AI_ANALYSIS_FAILED",
            jobFailureDelta: 1,
            platformConcentration: {
              ios: 0.68,
              android: 0.27,
              web: 0.05,
            },
          },
        ],
        aiRuntimeOperations: {
          aiProviderRouteLabel: "Gemini Developer API",
          aiServingAccessState: "Permission denied",
          aiBudgetThresholdState: "Healthy",
          aiModelUsageLineage: "gemini-3.1-flash-lite",
          aiModelUsageSummary: "gemini-3.1-flash-lite 78%, gemini-3.1-pro 22%",
          aiModelLimitSummary: "Warning",
        },
        observabilityAggregate: {
          releaseErrorSpikeSummary: "Contained",
          businessFlowCompletionRate: "Stable",
        },
      },
      meta: {
        requestId: "fixture_release_regression_req",
        generatedAt: "2026-06-12T00:00:00.000Z",
        sourceFreshness: {
          projection: "fresh",
          releases: "stale",
        },
      },
    },
  };
}

function mergeFixtureSet(fixtures?: Partial<AdminQueryFixtureSet>): AdminQueryFixtureSet {
  const defaults = createDefaultFixtureSet();

  return {
    overview: fixtures?.overview ?? defaults.overview,
    failedFlows: fixtures?.failedFlows ?? defaults.failedFlows,
    deviceKpis: fixtures?.deviceKpis ?? defaults.deviceKpis,
    userSummaries: fixtures?.userSummaries ?? defaults.userSummaries,
    userSummary: fixtures?.userSummary ?? defaults.userSummary,
    userDataDetail: fixtures?.userDataDetail ?? defaults.userDataDetail,
    mediaArtifactAccess: fixtures?.mediaArtifactAccess ?? defaults.mediaArtifactAccess,
    incidentLogArtifactAccess:
      fixtures?.incidentLogArtifactAccess ?? defaults.incidentLogArtifactAccess,
    jobs: fixtures?.jobs ?? defaults.jobs,
    releaseRegression: fixtures?.releaseRegression ?? defaults.releaseRegression,
  };
}

export function createFixtureAdminQueryAdapter(
  fixtures?: Partial<AdminQueryFixtureSet>,
): AdminQueryPort {
  const resolvedFixtures = mergeFixtureSet(fixtures);

  return {
    async getOverviewSummary(_query: AdminOverviewQuery): Promise<AdminQueryEnvelope<AdminOverviewSummary>> {
      return cloneFixture(resolvedFixtures.overview);
    },
    async searchFailedFlows(
      _query: AdminFailedFlowQuery,
    ): Promise<AdminQueryEnvelope<AdminFailedFlowPage>> {
      return cloneFixture(resolvedFixtures.failedFlows);
    },
    async searchDeviceKpis(
      _query: AdminDeviceKpiQuery,
    ): Promise<AdminQueryEnvelope<AdminDeviceKpiPage>> {
      return cloneFixture(resolvedFixtures.deviceKpis);
    },
    async searchUserSummaries(
      _query: AdminUserSummaryQuery,
    ): Promise<AdminQueryEnvelope<AdminUserSummaryPage>> {
      return cloneFixture(resolvedFixtures.userSummaries);
    },
    async getUserSummary(
      _input: AdminUserLookupInput,
    ): Promise<AdminQueryEnvelope<AdminUserSummary>> {
      return cloneFixture(resolvedFixtures.userSummary);
    },
    async getUserDataDetail(
      _input: AdminUserLookupInput,
    ): Promise<AdminQueryEnvelope<AdminUserDataDetail>> {
      return cloneFixture(resolvedFixtures.userDataDetail);
    },
    async issueUserMediaArtifactAccess(_input: {
      internalUserId: string;
      artifactId: string;
      action: AdminMediaArtifactAccessAction;
    }): Promise<AdminQueryEnvelope<AdminMediaArtifactAccessGrant>> {
      return cloneFixture(resolvedFixtures.mediaArtifactAccess);
    },
    async issueUserIncidentLogArtifactAccess(_input: {
      internalUserId: string;
      artifactId: string;
      action: AdminIncidentLogArtifactAccessAction;
    }): Promise<AdminQueryEnvelope<AdminIncidentLogArtifactAccessGrant>> {
      return cloneFixture(resolvedFixtures.incidentLogArtifactAccess);
    },
    async searchJobs(_query: AdminJobQuery): Promise<AdminQueryEnvelope<AdminJobPage>> {
      return cloneFixture(resolvedFixtures.jobs);
    },
    async getReleaseRegression(
      _query: AdminReleaseRegressionQuery,
    ): Promise<AdminQueryEnvelope<AdminReleaseRegression>> {
      return cloneFixture(resolvedFixtures.releaseRegression);
    },
  };
}
